<template>
  <div
    v-tooltip="{
      content: $t('PLACEMENT.ONDECK.SUBMISSION_PATH_API_ONLY'),
      onShow: () => !isApiSubmission,
    }"
    data-cy="placement-workflow-template-field"
  >
    <action-menu-v2
      ref="actionMenuRef"
      teleport-menu
      :disabled="!isApiSubmission"
      :check-for-overflow="true"
      close-menu-on-trigger-hide
    >
      <template #trigger>
        <lf-badge
          class="text-xs font-medium transition-all duration-300"
          :class="isApiSubmission ? 'cursor-pointer' : 'cursor-not-allowed'"
          is-rounded
        >
          <div class="flex space-x-2 overflow-hidden py-1">
            <span v-if="!isApiSubmission">-</span>
            <span
              v-else-if="localValue"
              class="overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {{ submissionPaths[localValue]?.title }}
            </span>
            <span v-else>
              {{ $t("COMMON.SELECT") }}
            </span>
            <span
              v-if="isApiSubmission"
              class="flex items-center text-gray-400"
            >
              <icon-base
                class="transform transition-all"
                height="6"
                width="10"
                :icon="IconDropdownArrow"
                :class="{ 'rotate-180': actionMenuRef?.showMenu }"
              />
            </span>
          </div>
        </lf-badge>
      </template>
      <li
        v-for="(option, key) in submissionPaths"
        :key="key"
        class="max-w-64 cursor-pointer rounded-md px-3 py-2"
        :class="
          localValue === key
            ? 'bg-primary-background-active'
            : 'hover:bg-gray-50'
        "
        @click="handleSelect(key as string)"
      >
        <div class="text-sm text-headline">{{ option.title }}</div>
        <div class="text-sm text-secondary">
          {{ option.description }}
        </div>
      </li>
    </action-menu-v2>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import ActionMenuV2 from "@/components/ui/ActionMenuV2.vue";
import LfBadge from "@/components/ui/LfBadge.vue";

import type { IFunder } from "@/models/funders";

const { model } = defineProps<{
  model: IFunder;
}>();

const emit = defineEmits<{
  change: [funderId: string, submissionPath: string];
}>();

const { t } = useI18n();

const actionMenuRef =
  useTemplateRef<InstanceType<typeof ActionMenuV2>>("actionMenuRef");

const localValue = ref(model.submission_path ?? "");

const isApiSubmission = computed(() => model.selected_method === 1);

const submissionPaths: Record<string, { title: string; description: string }> =
  {
    standard: {
      title: t("PLACEMENT.ONDECK.PATH_STANDARD"),
      description: t("PLACEMENT.ONDECK.PATH_STANDARD_DESC"),
    },
    pre_approval: {
      title: t("PLACEMENT.ONDECK.PRE_APPROVAL"),
      description: t("PLACEMENT.ONDECK.PATH_PRE_APPROVAL_DESC"),
    },
    auto: {
      title: t("PLACEMENT.ONDECK.PATH_AUTO"),
      description: t("PLACEMENT.ONDECK.PATH_AUTO_DESC"),
    },
  };

const handleSelect = (key: string) => {
  localValue.value = key;
  actionMenuRef.value?.closeMenu();
  emit("change", model.id, key);
};

watch(isApiSubmission, (newVal) => {
  if (!newVal) {
    localValue.value = "";
    emit("change", model.id, "");
  }
});
</script>
